<div class="employees-page">
  @if (loading) {
    <app-loader></app-loader>
  }

  <section class="overview-hero">
    <div class="hero-copy">
      <span class="hero-kicker">Workforce</span>

      <h1>Employee <span>Management</span></h1>

      <p>
        Track staffing, salon coverage, employee status, team roles, and
        operational readiness from one workforce workspace.
      </p>

      <div class="salon-scope-pill">
        <span>Salon view</span>
        <strong>{{ salonContext.selectionLabel() }}</strong>
      </div>
    </div>

    <div class="hero-actions">
      <button
        type="button"
        class="overview-btn overview-btn--dark"
        (click)="openEmployeeAdd()"
      >
        Add Employee
      </button>
    </div>
  </section>

  <section class="kpi-grid">
    <article class="kpi-card kpi-card--primary">
      <div>
        <span>Total Employees</span>
        <strong>{{ employeeCount || employees.length || 0 }}</strong>
        <p>
          {{ activeEmployeePercent }}% active workforce rate in the selected
          salon view.
        </p>
      </div>

      <img [src]="assetUrl + 'icons/users-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Active Employees</span>
        <strong>{{ activeEmployees }}</strong>
        <p>Employees currently active across the selected salons.</p>
      </div>

      <img [src]="assetUrl + 'icons/smile-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Salons Covered</span>
        <strong>{{ salonsCovered }}</strong>
        <p>Selected salon locations with staff coverage.</p>
      </div>

      <img [src]="assetUrl + 'icons/calendar-border.svg'" alt="" />
    </article>

    <article class="kpi-card">
      <div>
        <span>Departments</span>
        <strong>{{ departmentCount }}</strong>
        <p>Departments represented across the selected workforce.</p>
      </div>

      <img [src]="assetUrl + 'icons/users-border.svg'" alt="" />
    </article>
  </section>

  <section class="employee-grid">
    <article class="panel">
      <header class="panel-head">
        <div>
          <span class="panel-kicker">Team Directory</span>
          <h2>Workforce Directory</h2>
          <p>
            Review employees, salon coverage, status, and operating details
            within the selected salon view.
          </p>
        </div>

        <div class="panel-actions">
          <glo-control
            icon="search"
            placeholder="Search employees"
            classes="bg-gray-50"
            (valueChange)="onSearch($event)"
          >
          </glo-control>

          <select
            class="employee-select"
            [(ngModel)]="selectedStatusFilter"
            (change)="applyFilters()"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            class="employee-select"
            [(ngModel)]="selectedSalonFilter"
            (change)="applyFilters()"
          >
            <option value="all">All Selected Salons</option>
            @for (salon of salons; track salon.salon_id) {
              <option [value]="salon.salon_id">{{ salon.name }}</option>
            }
          </select>
        </div>
      </header>

      @if (filteredEmployees.length > 0) {
        <div class="table-shell">
          <table>
            <thead>
              <tr>
                <th>Employee</th>
                <th>Salon</th>
                <th>Role</th>
                <th>Contact</th>
                <th>Department</th>
                <th>Reporting To</th>
                <th>Working Days</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              @for (
                employee of viewEmployees;
                track resolveEmployeeId(employee)
              ) {
                <tr
                  [class.is-selected]="
                    resolveEmployeeId(selectedEmployee) ===
                    resolveEmployeeId(employee)
                  "
                  (click)="selectEmployee(employee)"
                >
                  <td>
                    <div class="employee-cell">
                      <div class="employee-avatar">
                        {{ getInitials(employee) }}
                      </div>

                      <div>
                        <strong>{{ resolveEmployeeFullName(employee) }}</strong>
                        <span
                          >ID {{ resolveEmployeeId(employee) || "N/A" }}</span
                        >
                      </div>
                    </div>
                  </td>

                  <td>{{ getEmployeeSalonName(employee) }}</td>

                  <td>
                    <span class="role-pill">
                      {{ getEmployeeRole(employee) }}
                    </span>
                  </td>

                  <td>
                    <div class="contact-cell">
                      <strong>{{ getEmployeeEmail(employee) }}</strong>
                      <span>{{ getEmployeePhone(employee) }}</span>
                    </div>
                  </td>

                  <td>{{ getEmployeeDepartment(employee) }}</td>

                  <td>{{ getEmployeeReportingTo(employee) }}</td>

                  <td>{{ getEmployeeWorkingDays(employee) }}</td>

                  <td>
                    <span
                      class="status-pill"
                      [class.status-pill--active]="employee.is_active"
                      [class.status-pill--inactive]="!employee.is_active"
                    >
                      {{ employee.is_active ? "Active" : "Inactive" }}
                    </span>
                  </td>

                  <td>
                    <div class="row-actions">
                      <button
                        type="button"
                        (click)="
                          editEmployee(employee); $event.stopPropagation()
                        "
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        class="danger"
                        (click)="
                          deleteEmployee(employee); $event.stopPropagation()
                        "
                      >
                        Deactivate
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <glo-pagination
          [totalItems]="filteredEmployees.length"
          [currentPage]="currentPage"
          [itemsPerPage]="itemsPerPage"
          [itemsPerPageOptions]="[10, 20, 50, 100]"
          (currentPageChange)="onPageChange($event)"
          (itemsPerPageChange)="onItemsPerPageChange($event)"
        >
        </glo-pagination>
      } @else {
        <div class="empty-state">
          <div class="empty-icon">E</div>

          <strong>No employees found</strong>

          <p>
            Try adjusting the filters or add a new employee to start building
            your workforce directory.
          </p>

          <button
            type="button"
            class="overview-btn overview-btn--dark"
            (click)="openEmployeeAdd()"
          >
            Add Employee
          </button>
        </div>
      }
    </article>

    @if (selectedEmployee) {
      <aside class="panel employee-profile-panel">
        <div class="profile-head">
          <div class="profile-avatar">
            {{ getInitials(selectedEmployee) }}
          </div>

          <div>
            <span class="panel-kicker">Employee Profile</span>

            <h3>{{ resolveEmployeeFullName(selectedEmployee) }}</h3>

            <p>{{ getEmployeeEmail(selectedEmployee) }}</p>
          </div>
        </div>

        <div class="profile-metrics">
          <div>
            <span>Status</span>
            <strong>
              {{ selectedEmployee.is_active ? "Active" : "Inactive" }}
            </strong>
          </div>

          <div>
            <span>Salon</span>
            <strong>{{ getEmployeeSalonName(selectedEmployee) }}</strong>
          </div>

          <div>
            <span>Role</span>
            <strong>{{ getEmployeeRole(selectedEmployee) }}</strong>
          </div>

          <div>
            <span>Department</span>
            <strong>{{ getEmployeeDepartment(selectedEmployee) }}</strong>
          </div>
        </div>

        <div class="profile-section">
          <h4>Contact Details</h4>

          <p>Email: {{ getEmployeeEmail(selectedEmployee) }}</p>
          <p>Phone: {{ getEmployeePhone(selectedEmployee) }}</p>
        </div>

        <div class="profile-section">
          <h4>Coverage Summary</h4>

          <p>Working Days: {{ getEmployeeWorkingDays(selectedEmployee) }}</p>
          <p>Reporting To: {{ getEmployeeReportingTo(selectedEmployee) }}</p>
        </div>

        <div class="profile-section">
          <h4>Suggested Action</h4>
          <p>{{ getEmployeeSuggestion(selectedEmployee) }}</p>
        </div>

        <div class="profile-actions">
          <button
            type="button"
            class="overview-btn overview-btn--dark"
            (click)="editEmployee(selectedEmployee)"
          >
            Edit
          </button>

          <button
            type="button"
            class="overview-btn overview-btn--light"
            (click)="deleteEmployee(selectedEmployee)"
          >
            Deactivate
          </button>
        </div>
      </aside>
    }
  </section>

  <div class="mobile-action">
    <button
      type="button"
      class="overview-btn overview-btn--dark"
      (click)="openEmployeeAdd()"
    >
      Add Employee
    </button>
  </div>
</div>
